import classNames from "classnames";
import { Button } from "components/ui/legacy/Button";
import { Modal, UnWrappedModal } from "components/ui/Modal";
import { useNormalizedError } from "hooks/useNormalizeError";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ApiError, { getErrorTexts } from "utils/errors/apiError";
import { EntitleError } from "utils/errors/entitleError";
import { getResolvedText, i18nText } from "utils/i18n";

import { TitleBody } from "../TitleBody";
import { useStyles } from "./styles";

type TModalActions = {
  closeText?: i18nText;
  onClose: () => Promise<void> | void;
  onRetry?: () => Promise<void> | void;
  retryText?: i18nText;
};

type TProps = TModalActions & TWithTestId & {
  className?: string;
  closeText?: i18nText;
  error: EntitleError | Error | null;
  isOpen: boolean;
  retryText?: i18nText;
  shouldCloseOnRetry?: boolean;
  useUnwrapped?: boolean;
};

export const ErrorModal: FC<TProps> = ({
  className,
  closeText: closeTextProp = i18nText("buttons.dismiss"),
  "data-testid": dataTestId,
  error,
  isOpen,
  onClose: onCloseProp,
  onRetry: onRetryProp,
  retryText: retryTextProp = i18nText("buttons.tryAgain"),
  shouldCloseOnRetry = false,
  useUnwrapped = false,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const normalizedError = useNormalizedError(error);

  const { closeText, onClose, onRetry, retryText } = useMemo<TModalActions>(() => {
    if (!(normalizedError instanceof EntitleError)) {
      return { closeText: closeTextProp, onClose: onCloseProp, onRetry: onRetryProp, retryText: retryTextProp };
    }
    const { closeText, onClose, onRetry, retryText } = normalizedError.getActions();
    return {
      closeText: closeText === null ? undefined : closeText || closeTextProp,
      onClose: onClose || onCloseProp,
      onRetry: onRetry || onRetryProp,
      retryText: retryText === null ? undefined : retryText || retryTextProp,
    };
  }, [onCloseProp, onRetryProp, retryTextProp, closeTextProp, normalizedError]);

  const handleRetry = useCallback(async () => {
    if (!onRetry) return;
    await onRetry();
    if (shouldCloseOnRetry) await onClose();
  }, [onClose, onRetry, shouldCloseOnRetry]);

  const { message, title } = useMemo(() => {
    if (normalizedError instanceof ApiError || !(normalizedError instanceof EntitleError)) {
      return getErrorTexts(normalizedError);
    }
    return normalizedError.getTexts();
  }, [normalizedError]);

  useEffect(() => {
    if (isOpen) {
      (document.activeElement as HTMLElement)?.blur();
    }
  }, [isOpen]);

  const modalProps = useMemo(() => {
    return {
      actions: (
        <>
          {closeText && (
            <Button onClick={onClose} size="medium" variant={onRetry ? "secondary" : "primary"}>
              {getResolvedText(t, closeText)}
            </Button>
          )}

          {onRetry && retryText && (
            <Button onClick={handleRetry} size="medium" variant="primary">
              {getResolvedText(t, retryText)}
            </Button>
          )}
        </>
      ),
      className: classNames(classes.modal, className),
      content: <TitleBody body={message} className={classes.content} size="large" title={title} />,
      isOpen,
      onClose,
    };
  }, [
    className,
    classes.content,
    classes.modal,
    closeText,
    handleRetry,
    isOpen,
    message,
    onClose,
    onRetry,
    retryText,
    t,
    title,
  ]);

  return useUnwrapped
    ? (
        modalProps.isOpen
          ? (
              <UnWrappedModal {...modalProps} data-testid={dataTestId} parentElement={document.body} />
            )
          : null
      )
    : (
        <Modal {...modalProps} data-testid={dataTestId} />
      );
};
